import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApplicationStore } from "../../../librettoEditor/store/ApplicationStore";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import CompositionBridge from "../../../librettoEditor/CompositionBridge";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { DEFAULT_SPEAKER_COLORS, ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { createDetachedExportComposition } from "../../../librettoEditor/services/DetachedExportCompositionService";
import { MbEncoder } from "../../../engine/encoders/mediabunny.encoder";
import { MbAudioEncoder } from "../../../engine/encoders/audio.encoder";
import { ExportStore, VIDEO_QUALITY_PRESETS } from "../../../librettoEditor/store/ExportStore";
import { LibrettoProjectTypes } from "../../../utils/utils";
import type { DecomposeExportRow, DecomposeSpeakerView, DecomposeWorkflowStep } from "../DecomposeClipSidebar";
import { buildDetachedExportSeed } from "./buildDetachedExportSeed";
import { resolveDecomposeAudioExportSettings } from "./decomposeExportDefaults";

type DecomposeEligibilityState = {
    isReady: boolean;
    enabled: boolean;
    reason: string | null;
    scriptClipId: string | null;
    sourceTrackId: string | null;
    sourceHasVideo: boolean;
    sourceHasAudio: boolean;
    sourceIndex: number;
    speakerDiarizationIds: string[];
    speakerGroups: DecomposeSpeakerRuntime[];
    unassignedDiarizationIds: string[];
};

type DecomposeSpeakerRuntime = {
    key: string;
    sourceIndex: number;
    diarizationSpeakerIds: string[];
    speakerName: string;
    color?: string;
    speakerEntityId?: string;
    crop?: { x: number; y: number; width: number; height: number };
};

type DecomposeResolvedSpeakerAssignment = {
    diarizationSpeakerId: string;
    speakerName: string;
    color?: string;
    speakerEntityId?: string;
    assigned: boolean;
};

type DecomposeResolvedSpeakers = {
    assignments: DecomposeResolvedSpeakerAssignment[];
    groupedSpeakers: DecomposeSpeakerRuntime[];
    unassignedDiarizationIds: string[];
};

type DecomposeExportRuntime = {
    key: string;
    speakerName: string;
    status: "pending" | "exporting" | "uploading" | "complete" | "error";
    progressPct?: number;
    message?: string;
    assetId?: string;
};

type DecomposeWorkflowState = {
    step: DecomposeWorkflowStep;
    speakers: DecomposeSpeakerRuntime[];
    currentCropSpeakerIndex: number | null;
    exports: DecomposeExportRuntime[];
    error: string | null;
};

type DecomposeSourceEntrySegment = {
    offsetSec: number;
    playbackRate: number;
    rangeInSec: number;
    rangeOutSec: number;
};

const MIN_DECOMPOSE_SPEAKERS = 2;
const MAX_DECOMPOSE_SPEAKERS = 4;

const DEFAULT_DECOMPOSE_ELIGIBILITY: DecomposeEligibilityState = {
    isReady: false,
    enabled: false,
    reason: "Checking clip eligibility...",
    scriptClipId: null,
    sourceTrackId: null,
    sourceHasVideo: false,
    sourceHasAudio: false,
    sourceIndex: 0,
    speakerDiarizationIds: [],
    speakerGroups: [],
    unassignedDiarizationIds: [],
};

const collectTranscriptSpeakerIds = (transcriptJson: any): string[] => {
    const out: string[] = [];
    const seen = new Set<string>();
    const words = Array.isArray(transcriptJson?.words) ? transcriptJson.words : [];
    for (const w of words) {
        if (!w) continue;
        if ((w.type ?? "word") !== "word") continue;
        const speakerRaw = w.speakerId ?? w.speaker_id;
        const speakerId = String(speakerRaw ?? "").trim();
        if (!speakerId || seen.has(speakerId)) continue;
        seen.add(speakerId);
        out.push(speakerId);
    }
    return out;
};

const resolveScriptSourceEntrySegments = (args: {
    scriptClip: any;
    sourceIndex: number;
    sourceDuration: number;
}): DecomposeSourceEntrySegment[] => {
    const sourceDuration = Number(args.sourceDuration ?? 0);
    const audioEntries: any[] =
        typeof args.scriptClip?.getAudioEntries === "function" ? args.scriptClip.getAudioEntries() : [];
    const videoEntries: any[] =
        typeof args.scriptClip?.getVideoEntries === "function" ? args.scriptClip.getVideoEntries() : [];
    const entry = audioEntries[args.sourceIndex] ?? videoEntries[args.sourceIndex];
    if (!entry) return [];

    const segments: DecomposeSourceEntrySegment[] = [];
    if (Array.isArray((entry as any)?.segments) && (entry as any).segments.length > 0) {
        for (const seg of (entry as any).segments as any[]) {
            const offsetSec = Number(seg?.offsetSec ?? 0);
            const playbackRate = Number(seg?.playbackRate ?? (entry as any)?.playbackRate ?? 1.0) || 1.0;
            const rangeInSec = Number(seg?.range?.inSec ?? 0);
            const rangeOutSec = Number(seg?.range?.outSec ?? 0);
            if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
            if (!Number.isFinite(rangeInSec) || !Number.isFinite(rangeOutSec) || rangeOutSec <= rangeInSec) continue;
            segments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
        }
        return segments;
    }

    const fallbackRange = (entry as any)?.range;
    const offsetSec = Number((entry as any)?.offsetSec ?? 0) || 0;
    const playbackRate = Number((entry as any)?.playbackRate ?? 1.0) || 1.0;
    const rangeInSec = Number(fallbackRange?.inSec ?? 0);
    const rangeOutSec = Number(fallbackRange?.outSec ?? sourceDuration);
    if (Number.isFinite(rangeInSec) && Number.isFinite(rangeOutSec) && rangeOutSec > rangeInSec) {
        segments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
    }
    return segments;
};

const collectActiveTranscriptSpeakerIds = (args: {
    scriptClip: any;
    sourceIndex: number;
    sourceDuration: number;
    transcriptJson: any;
}): string[] => {
    const transcriptWords = Array.isArray(args.transcriptJson?.words) ? args.transcriptJson.words : [];
    if (transcriptWords.length === 0) return [];

    const entrySegments = resolveScriptSourceEntrySegments({
        scriptClip: args.scriptClip,
        sourceIndex: args.sourceIndex,
        sourceDuration: args.sourceDuration,
    });

    // Fallback to transcript-level counting when source segments are temporarily unavailable.
    if (entrySegments.length === 0) {
        return collectTranscriptSpeakerIds(args.transcriptJson);
    }

    const out: string[] = [];
    const seen = new Set<string>();
    const DEFAULT_WORD_DURATION_SEC = 0.35;
    const MAX_WORD_DURATION_SEC = 2.5;

    for (const seg of entrySegments) {
        for (const word of transcriptWords) {
            if (!word || (word.type ?? "word") !== "word") continue;
            const diarizationSpeakerId = String(word.speakerId ?? word.speaker_id ?? "").trim();
            if (!diarizationSpeakerId) continue;

            const wordStart = Number(word.t0 ?? word.start ?? NaN);
            let wordEnd = Number(word.t1 ?? word.end ?? wordStart);
            if (!Number.isFinite(wordStart)) continue;
            if (!Number.isFinite(wordEnd) || wordEnd <= wordStart) {
                wordEnd = wordStart + DEFAULT_WORD_DURATION_SEC;
            }
            if (wordEnd - wordStart > MAX_WORD_DURATION_SEC) {
                wordEnd = wordStart + MAX_WORD_DURATION_SEC;
            }

            if (wordStart < seg.rangeInSec || wordEnd > seg.rangeOutSec) continue;

            const adjustedStart = (wordStart - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
            const adjustedEnd = (wordEnd - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
            if (!Number.isFinite(adjustedStart) || !Number.isFinite(adjustedEnd) || adjustedEnd <= adjustedStart) continue;
            if (ScriptLayerStore.isTimeInDeleteCut(adjustedStart) || ScriptLayerStore.isTimeInGap(adjustedStart)) continue;

            if (seen.has(diarizationSpeakerId)) continue;
            seen.add(diarizationSpeakerId);
            out.push(diarizationSpeakerId);
        }
    }

    return out;
};

const normalizeDecomposeCrop = (
    crop: { x: number; y: number; width: number; height: number } | undefined,
): { x: number; y: number; width: number; height: number } | undefined => {
    if (!crop) return undefined;
    const x = Number(crop.x ?? 0);
    const y = Number(crop.y ?? 0);
    const width = Number(crop.width ?? 1);
    const height = Number(crop.height ?? 1);
    if (![x, y, width, height].every(Number.isFinite)) return undefined;
    if (Math.abs(x) < 1e-6 && Math.abs(y) < 1e-6 && Math.abs(width - 1) < 1e-6 && Math.abs(height - 1) < 1e-6) {
        return undefined;
    }
    return {
        x: Math.max(0, Math.min(1, x)),
        y: Math.max(0, Math.min(1, y)),
        width: Math.max(0, Math.min(1, width)),
        height: Math.max(0, Math.min(1, height)),
    };
};

const sanitizeFilenamePart = (value: string): string => {
    const raw = String(value ?? "").trim();
    if (!raw) return "speaker";
    const sanitized = raw.replace(/[^\w.\- ()]/g, "_").trim();
    return sanitized || "speaker";
};

const overrideSpecificity = (o: any): number => {
    let score = 0;
    if (o?.sourceIndex != null) score += 1;
    if ((o as any)?.diarizationSpeakerId) score += 2;
    return score;
};

const overrideDuration = (o: any): number => {
    const start = Number(o?.startSec ?? 0);
    const end = Number(o?.endSec ?? 0);
    return Math.max(0, end - start);
};

const chooseBetterOverride = (a: any, b: any): any => {
    const durA = overrideDuration(a);
    const durB = overrideDuration(b);
    if (durA !== durB) return durB < durA ? b : a;

    const specA = overrideSpecificity(a);
    const specB = overrideSpecificity(b);
    if (specA !== specB) return specB > specA ? b : a;

    if (Number(a?.startSec ?? 0) !== Number(b?.startSec ?? 0)) {
        return Number(b?.startSec ?? 0) > Number(a?.startSec ?? 0) ? b : a;
    }
    return String(b?.id ?? "") > String(a?.id ?? "") ? b : a;
};

type UseDecomposeClipWorkflowArgs = {
    isEnabled: boolean;
    selectedCompositionId: string | null;
    selectedCompositionTitle: string | null;
    getTranscript: (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;
    resolveAssetUrl: (assetId: string, type: "audio" | "video" | "image") => Promise<string | undefined>;
    getEnhancedAudioUrl: (args: { trackId: string }) => Promise<string | undefined>;
    uploadAsset: (args: {
        projectId: string;
        projectType: any;
        file: File;
        filetype: string;
        languageCode: string;
        assetTitle?: string;
    }) => Promise<any>;
};

type UseDecomposeClipWorkflowState = {
    disabledReason: string | null;
    hasVideo: boolean;
    step: DecomposeWorkflowStep;
    hasWorkflow: boolean;
    isWorkflowActive: boolean;
    isProcessing: boolean;
    speakers: DecomposeSpeakerView[];
    currentCropSpeakerIndex?: number;
    exports: DecomposeExportRow[];
    error: string | null;
    isCropModeActive: boolean;
    startWorkflow: () => Promise<boolean>;
    resetWorkflow: () => void;
    validateAssignments: () => void;
    startCurrentCrop: () => void;
    onCropDoneCapture: (crop: { x: number; y: number; width: number; height: number }, scope: "current" | "all") => boolean | void;
};

export const useDecomposeClipWorkflow = ({
    isEnabled,
    selectedCompositionId,
    selectedCompositionTitle,
    getTranscript,
    resolveAssetUrl,
    getEnhancedAudioUrl,
    uploadAsset,
}: UseDecomposeClipWorkflowArgs): UseDecomposeClipWorkflowState => {
    const [eligibility, setEligibility] = useState<DecomposeEligibilityState>(DEFAULT_DECOMPOSE_ELIGIBILITY);
    const [workflow, setWorkflow] = useState<DecomposeWorkflowState | null>(null);
    const eligibilityReqSeqRef = useRef(0);
    const eligibilityCacheKeyRef = useRef<string>("");
    const eligibilityCacheValueRef = useRef<DecomposeEligibilityState>(DEFAULT_DECOMPOSE_ELIGIBILITY);
    const isMountedRef = useRef(true);
    const getTranscriptRef = useRef(getTranscript);
    const resolveAssetUrlRef = useRef(resolveAssetUrl);
    const getEnhancedAudioUrlRef = useRef(getEnhancedAudioUrl);
    const uploadAssetRef = useRef(uploadAsset);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        getTranscriptRef.current = getTranscript;
        resolveAssetUrlRef.current = resolveAssetUrl;
        getEnhancedAudioUrlRef.current = getEnhancedAudioUrl;
        uploadAssetRef.current = uploadAsset;
    }, [getTranscript, resolveAssetUrl, getEnhancedAudioUrl, uploadAsset]);

    const projectId = String(ProjectAssetsStore.projectId ?? "").trim();
    const speakerOverridesVersion = Number(ScriptLayerStore.speakerOverridesVersion ?? 0);
    const cutsVersion = Number(ScriptLayerStore.cutsVersion ?? 0);
    const projectTracksSignature = useMemo(() => {
        const tracks = Array.isArray(ProjectAssetsStore.projectTracks) ? ProjectAssetsStore.projectTracks : [];
        return tracks
            .map((t: any) => {
                const id = String(t?.trackId || t?.id || "");
                const transcriptId = String(t?.activeTranscriptId ?? "");
                const status = String(t?.status ?? "");
                return `${id}:${transcriptId}:${status}`;
            })
            .join("|");
    }, [ProjectAssetsStore.projectTracks]);

    const resolveSpeakers = useCallback(
        (speakerIds: string[], sourceIndex: number): DecomposeResolvedSpeakers => {
            const overrides = Array.isArray(ScriptLayerStore.speakerOverrides)
                ? (ScriptLayerStore.speakerOverrides as any[])
                : [];

            const pickOverride = (speakerId: string): any | null => {
                const matching = overrides.filter((o: any) => {
                    const overrideSourceIndex = Number.isFinite((o as any)?.sourceIndex)
                        ? Number((o as any).sourceIndex)
                        : undefined;
                    if (overrideSourceIndex != null && overrideSourceIndex !== sourceIndex) return false;
                    const diarizationSpeakerId = String((o as any)?.diarizationSpeakerId ?? "").trim();
                    if (!diarizationSpeakerId || diarizationSpeakerId !== speakerId) return false;
                    const speakerName = String((o as any)?.speakerName ?? "").trim();
                    if (!speakerName || speakerName === "__hidden__") return false;
                    return true;
                });
                if (!matching.length) return null;
                return matching.sort((a: any, b: any) => {
                    const durA = Number((a as any)?.endSec ?? 0) - Number((a as any)?.startSec ?? 0);
                    const durB = Number((b as any)?.endSec ?? 0) - Number((b as any)?.startSec ?? 0);
                    if (durA !== durB) return durB - durA;
                    return Number((b as any)?.startSec ?? 0) - Number((a as any)?.startSec ?? 0);
                })[0];
            };

            const usedColors = new Set<string>(
                overrides
                    .map((o: any) => (typeof o?.color === "string" ? String(o.color).trim() : ""))
                    .filter(Boolean),
            );

            const assignments: DecomposeResolvedSpeakerAssignment[] = speakerIds.map((speakerId) => {
                const override = pickOverride(speakerId);
                const assignedName = String((override as any)?.speakerName ?? "").trim();
                const overrideColor =
                    typeof (override as any)?.color === "string" && String((override as any).color).trim()
                        ? String((override as any).color).trim()
                        : undefined;
                const diarizationMatch = String(speakerId).match(/(\d+)$/);
                const diarizationColor =
                    diarizationMatch && DEFAULT_SPEAKER_COLORS.length > 0
                        ? DEFAULT_SPEAKER_COLORS[Number(diarizationMatch[1]) % DEFAULT_SPEAKER_COLORS.length]
                        : undefined;
                let resolvedColor = overrideColor || diarizationColor;
                if (!resolvedColor || usedColors.has(resolvedColor)) {
                    resolvedColor = ScriptLayerStore.getNextSpeakerColor(Array.from(usedColors));
                }
                if (resolvedColor) usedColors.add(resolvedColor);
                const normalizedName = assignedName.trim();

                return {
                    diarizationSpeakerId: speakerId,
                    speakerName: normalizedName || "New Speaker",
                    color: resolvedColor,
                    speakerEntityId:
                        typeof (override as any)?.speakerId === "string" && String((override as any).speakerId).trim()
                            ? String((override as any).speakerId).trim()
                            : undefined,
                    assigned: !!normalizedName && normalizedName !== "New Speaker",
                };
            });

            const unassignedDiarizationIds = assignments.filter((a) => !a.assigned).map((a) => a.diarizationSpeakerId);
            const groupedMap = new Map<string, {
                order: number;
                speakerName: string;
                color?: string;
                speakerEntityId?: string;
                diarizationSpeakerIds: string[];
            }>();

            for (let idx = 0; idx < assignments.length; idx++) {
                const assignment = assignments[idx];
                if (!assignment.assigned) continue;
                const speakerEntityId = String(assignment.speakerEntityId ?? "").trim() || undefined;
                const normalizedName = String(assignment.speakerName ?? "").trim();
                if (!normalizedName || normalizedName === "New Speaker") continue;
                const groupKey = speakerEntityId
                    ? `speaker:${speakerEntityId}`
                    : `name:${normalizedName.toLowerCase()}`;

                if (!groupedMap.has(groupKey)) {
                    groupedMap.set(groupKey, {
                        order: idx,
                        speakerName: normalizedName,
                        color: assignment.color,
                        speakerEntityId,
                        diarizationSpeakerIds: [assignment.diarizationSpeakerId],
                    });
                    continue;
                }

                const existing = groupedMap.get(groupKey)!;
                if (!existing.diarizationSpeakerIds.includes(assignment.diarizationSpeakerId)) {
                    existing.diarizationSpeakerIds.push(assignment.diarizationSpeakerId);
                }
                if (!existing.color && assignment.color) existing.color = assignment.color;
                if (!existing.speakerEntityId && speakerEntityId) existing.speakerEntityId = speakerEntityId;
            }

            const groupedSpeakers: DecomposeSpeakerRuntime[] = Array.from(groupedMap.entries())
                .sort((a, b) => a[1].order - b[1].order)
                .map(([groupKey, value]) => ({
                    key: `${sourceIndex}:${groupKey}`,
                    sourceIndex,
                    diarizationSpeakerIds: value.diarizationSpeakerIds,
                    speakerName: value.speakerName,
                    color: value.color,
                    speakerEntityId: value.speakerEntityId,
                }));

            return {
                assignments,
                groupedSpeakers,
                unassignedDiarizationIds,
            };
        },
        [speakerOverridesVersion],
    );

    const evaluateEligibility = useCallback(
        async (opts?: { force?: boolean }): Promise<DecomposeEligibilityState> => {
            const reqSeq = ++eligibilityReqSeqRef.current;
            const finish = (next: DecomposeEligibilityState) => {
                if (!isMountedRef.current) return next;
                if (reqSeq !== eligibilityReqSeqRef.current) return next;
                setEligibility(next);
                return next;
            };

            finish({
                ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                isReady: false,
                enabled: false,
                reason: "Checking clip eligibility...",
            });

            const scriptClip: any = CompositionService.getScriptLayerClip?.();
            const scriptClipId = String(scriptClip?.id ?? "").trim();
            if (!scriptClip || !scriptClipId || String(scriptClip?.type ?? "") !== "script") {
                return finish({
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: "Requires an active script clip.",
                });
            }

            const inputConfigs: any[] = typeof scriptClip.getInputConfigs === "function" ? scriptClip.getInputConfigs() : [];
            if (inputConfigs.length !== 1) {
                return finish({
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: "Requires a script clip created from exactly one source.",
                    scriptClipId,
                });
            }

            const sourceIndex = 0;
            const sourceTrackId = String(inputConfigs[0]?.assetId ?? "").trim();
            const videoEntries: any[] = typeof scriptClip.getVideoEntries === "function" ? scriptClip.getVideoEntries() : [];
            const audioEntries: any[] = typeof scriptClip.getAudioEntries === "function" ? scriptClip.getAudioEntries() : [];
            const sourceHasVideo = videoEntries.length > 0;
            const sourceHasAudio = audioEntries.length > 0;

            if (!sourceHasVideo && !sourceHasAudio) {
                return finish({
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: "Script clip source must include audio or video.",
                    scriptClipId,
                    sourceTrackId: sourceTrackId || null,
                    sourceHasVideo,
                    sourceHasAudio,
                    sourceIndex,
                });
            }

            if (!sourceTrackId) {
                return finish({
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: "Could not resolve the source track for this script clip.",
                    scriptClipId,
                    sourceTrackId: null,
                    sourceHasVideo,
                    sourceHasAudio,
                    sourceIndex,
                });
            }

            if (!projectId) {
                return finish({
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: "Missing project context.",
                    scriptClipId,
                    sourceTrackId,
                    sourceHasVideo,
                    sourceHasAudio,
                    sourceIndex,
                });
            }

            const track = (ProjectAssetsStore.projectTracks || []).find(
                (t: any) => String(t?.trackId || t?.id || "").trim() === sourceTrackId,
            );
            const transcriptId = String(track?.activeTranscriptId ?? "").trim();
            if (!transcriptId) {
                return finish({
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: "Transcript must be ready before separating speakers.",
                    scriptClipId,
                    sourceTrackId,
                    sourceHasVideo,
                    sourceHasAudio,
                    sourceIndex,
                });
            }

            const cacheKey = `${projectId}:${scriptClipId}:${sourceTrackId}:${transcriptId}:${speakerOverridesVersion}:${cutsVersion}`;
            if (!opts?.force && cacheKey === eligibilityCacheKeyRef.current) {
                return finish(eligibilityCacheValueRef.current);
            }

            let transcriptJson: any = null;
            try {
                transcriptJson = await getTranscriptRef.current({
                    transcriptId,
                    projectId,
                });
            } catch {
                transcriptJson = null;
            }

            const sourceDuration = Number(track?.duration ?? 0);
            const speakerDiarizationIds = collectActiveTranscriptSpeakerIds({
                scriptClip,
                sourceIndex,
                sourceDuration,
                transcriptJson,
            });
            const resolvedSpeakers = resolveSpeakers(speakerDiarizationIds, sourceIndex);
            let next: DecomposeEligibilityState;
            if (resolvedSpeakers.unassignedDiarizationIds.length > 0) {
                const countLabel =
                    resolvedSpeakers.unassignedDiarizationIds.length === 1
                        ? "1 unassigned tag"
                        : `${resolvedSpeakers.unassignedDiarizationIds.length} unassigned tags`;
                next = {
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: `Assign all speaker tags before separating speakers (${countLabel}).`,
                    scriptClipId,
                    sourceTrackId,
                    sourceHasVideo,
                    sourceHasAudio,
                    sourceIndex,
                    speakerDiarizationIds,
                    speakerGroups: resolvedSpeakers.groupedSpeakers,
                    unassignedDiarizationIds: resolvedSpeakers.unassignedDiarizationIds,
                };
            } else if (
                resolvedSpeakers.groupedSpeakers.length < MIN_DECOMPOSE_SPEAKERS ||
                resolvedSpeakers.groupedSpeakers.length > MAX_DECOMPOSE_SPEAKERS
            ) {
                const countLabel =
                    resolvedSpeakers.groupedSpeakers.length === 1
                        ? "1 assigned speaker"
                        : `${resolvedSpeakers.groupedSpeakers.length} assigned speakers`;
                next = {
                    ...DEFAULT_DECOMPOSE_ELIGIBILITY,
                    isReady: true,
                    enabled: false,
                    reason: `Requires ${MIN_DECOMPOSE_SPEAKERS}-${MAX_DECOMPOSE_SPEAKERS} assigned speakers (${countLabel}).`,
                    scriptClipId,
                    sourceTrackId,
                    sourceHasVideo,
                    sourceHasAudio,
                    sourceIndex,
                    speakerDiarizationIds,
                    speakerGroups: resolvedSpeakers.groupedSpeakers,
                    unassignedDiarizationIds: resolvedSpeakers.unassignedDiarizationIds,
                };
            } else {
                next = {
                    isReady: true,
                    enabled: true,
                    reason: null,
                    scriptClipId,
                    sourceTrackId,
                    sourceHasVideo,
                    sourceHasAudio,
                    sourceIndex,
                    speakerDiarizationIds,
                    speakerGroups: resolvedSpeakers.groupedSpeakers,
                    unassignedDiarizationIds: resolvedSpeakers.unassignedDiarizationIds,
                };
            }

            eligibilityCacheKeyRef.current = cacheKey;
            eligibilityCacheValueRef.current = next;
            return finish(next);
        },
        [cutsVersion, projectId, projectTracksSignature, resolveSpeakers, speakerOverridesVersion],
    );

    useEffect(() => {
        if (!isEnabled) return;
        void evaluateEligibility();
    }, [cutsVersion, isEnabled, evaluateEligibility, selectedCompositionId, projectTracksSignature, speakerOverridesVersion]);

    const resetWorkflow = useCallback(() => {
        setWorkflow(null);
        if (ApplicationStore.isCropMode) {
            try { ApplicationStore.exitCropMode(); } catch {}
            try { CompositionService.recomputeFrame(); } catch {}
        }
    }, []);

    const collectSpeakerRanges = useCallback(
        async (args: {
            scriptClip: any;
            sourceTrackId: string;
            sourceIndex: number;
            speakers: DecomposeSpeakerRuntime[];
        }): Promise<Map<string, Array<{ startSec: number; endSec: number }>>> => {
            if (!projectId) {
                throw new Error("Missing project id.");
            }

            const sourceTrack = (ProjectAssetsStore.projectTracks || []).find(
                (t: any) => String(t?.trackId || t?.id || "").trim() === String(args.sourceTrackId || "").trim(),
            );
            const transcriptId = String(sourceTrack?.activeTranscriptId ?? "").trim();
            if (!transcriptId) {
                throw new Error("Transcript is not ready for the source track.");
            }

            const transcriptJson = await getTranscriptRef.current({
                transcriptId,
                projectId,
            });
            const transcriptWords = Array.isArray(transcriptJson?.words) ? transcriptJson.words : [];
            if (transcriptWords.length === 0) {
                throw new Error("Transcript has no words.");
            }

            const sourceDuration = Number(sourceTrack?.duration ?? 0);
            const entrySegments = resolveScriptSourceEntrySegments({
                scriptClip: args.scriptClip,
                sourceIndex: args.sourceIndex,
                sourceDuration,
            });

            if (entrySegments.length === 0) {
                throw new Error("Script clip source segments are unavailable.");
            }

            const bySpeaker = new Map<string, Array<{ startSec: number; endSec: number }>>();
            const diarizationToSpeakerKey = new Map<string, string>();
            const speakerIdToSpeakerKey = new Map<string, string>();
            const speakerNameToSpeakerKey = new Map<string, string>();
            for (const speaker of args.speakers ?? []) {
                const speakerKey = String(speaker?.key ?? "").trim();
                if (!speakerKey) continue;
                for (const diarizationSpeakerId of speaker.diarizationSpeakerIds ?? []) {
                    const diarizationKey = String(diarizationSpeakerId ?? "").trim();
                    if (diarizationKey && !diarizationToSpeakerKey.has(diarizationKey)) {
                        diarizationToSpeakerKey.set(diarizationKey, speakerKey);
                    }
                }
                const speakerId = String(speaker?.speakerEntityId ?? "").trim();
                if (speakerId && !speakerIdToSpeakerKey.has(speakerId)) {
                    speakerIdToSpeakerKey.set(speakerId, speakerKey);
                }
                const speakerName = String(speaker?.speakerName ?? "").trim().toLowerCase();
                if (speakerName && speakerName !== "new speaker" && !speakerNameToSpeakerKey.has(speakerName)) {
                    speakerNameToSpeakerKey.set(speakerName, speakerKey);
                }
            }

            const overrides = Array.isArray(ScriptLayerStore.speakerOverrides)
                ? (ScriptLayerStore.speakerOverrides as any[])
                : [];
            const DEFAULT_WORD_DURATION_SEC = 0.35;
            const MAX_WORD_DURATION_SEC = 2.5;
            const mappedWords: Array<{ startSec: number; endSec: number; speakerKey: string }> = [];

            const resolveSpeakerKeyForWord = (
                wordTimeSec: number,
                diarizationSpeakerId: string,
            ): string | undefined => {
                const matchingOverrides = overrides.filter((override) => {
                    const startSec = Number((override as any)?.startSec ?? NaN);
                    const endSec = Number((override as any)?.endSec ?? NaN);
                    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec) return false;
                    if (wordTimeSec < startSec || wordTimeSec >= endSec) return false;
                    const overrideSourceIndex = Number.isFinite((override as any)?.sourceIndex)
                        ? Number((override as any)?.sourceIndex)
                        : undefined;
                    if (overrideSourceIndex != null && overrideSourceIndex !== args.sourceIndex) return false;
                    const overrideDiarizationSpeakerId = String((override as any)?.diarizationSpeakerId ?? "").trim();
                    if (overrideDiarizationSpeakerId && overrideDiarizationSpeakerId !== diarizationSpeakerId) return false;
                    return true;
                });

                if (matchingOverrides.length > 0) {
                    const best = matchingOverrides.reduce(chooseBetterOverride);
                    const overrideSpeakerId = String((best as any)?.speakerId ?? "").trim();
                    if (overrideSpeakerId && speakerIdToSpeakerKey.has(overrideSpeakerId)) {
                        return speakerIdToSpeakerKey.get(overrideSpeakerId);
                    }

                    const overrideSpeakerName = String((best as any)?.speakerName ?? "").trim().toLowerCase();
                    if (overrideSpeakerName && overrideSpeakerName !== "__hidden__" && speakerNameToSpeakerKey.has(overrideSpeakerName)) {
                        return speakerNameToSpeakerKey.get(overrideSpeakerName);
                    }
                }

                return diarizationToSpeakerKey.get(diarizationSpeakerId);
            };

            for (const seg of entrySegments) {
                for (const word of transcriptWords) {
                    if (!word || (word.type ?? "word") !== "word") continue;
                    const diarizationSpeakerId = String(word.speakerId ?? word.speaker_id ?? "").trim();
                    if (!diarizationSpeakerId) continue;

                    const wordStart = Number(word.t0 ?? word.start ?? NaN);
                    let wordEnd = Number(word.t1 ?? word.end ?? wordStart);
                    if (!Number.isFinite(wordStart)) continue;
                    if (!Number.isFinite(wordEnd) || wordEnd <= wordStart) {
                        wordEnd = wordStart + DEFAULT_WORD_DURATION_SEC;
                    }
                    if (wordEnd - wordStart > MAX_WORD_DURATION_SEC) {
                        wordEnd = wordStart + MAX_WORD_DURATION_SEC;
                    }

                    if (wordStart < seg.rangeInSec || wordEnd > seg.rangeOutSec) continue;

                    const adjustedStart = (wordStart - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
                    const adjustedEnd = (wordEnd - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
                    if (!Number.isFinite(adjustedStart) || !Number.isFinite(adjustedEnd) || adjustedEnd <= adjustedStart) continue;
                    if (ScriptLayerStore.isTimeInDeleteCut(adjustedStart) || ScriptLayerStore.isTimeInGap(adjustedStart)) continue;
                    const speakerKey = resolveSpeakerKeyForWord(adjustedStart, diarizationSpeakerId);
                    if (!speakerKey) continue;
                    mappedWords.push({ startSec: adjustedStart, endSec: adjustedEnd, speakerKey });
                }
            }

            if (mappedWords.length === 0) {
                return bySpeaker;
            }

            mappedWords.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
            let currentSegment: { speakerKey: string; startSec: number; endSec: number } | null = null;
            const pushCurrentSegment = () => {
                if (!currentSegment) return;
                if (!bySpeaker.has(currentSegment.speakerKey)) {
                    bySpeaker.set(currentSegment.speakerKey, []);
                }
                bySpeaker.get(currentSegment.speakerKey)!.push({
                    startSec: currentSegment.startSec,
                    endSec: currentSegment.endSec,
                });
            };

            for (const word of mappedWords) {
                if (!currentSegment || currentSegment.speakerKey !== word.speakerKey) {
                    pushCurrentSegment();
                    currentSegment = {
                        speakerKey: word.speakerKey,
                        startSec: word.startSec,
                        endSec: word.endSec,
                    };
                    continue;
                }
                currentSegment.endSec = Math.max(currentSegment.endSec, word.endSec);
            }
            pushCurrentSegment();

            for (const [speakerId, ranges] of bySpeaker.entries()) {
                ranges.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
                const merged: Array<{ startSec: number; endSec: number }> = [];
                const EPS = 1e-3;
                for (const r of ranges) {
                    if (!merged.length) {
                        merged.push({ ...r });
                        continue;
                    }
                    const last = merged[merged.length - 1];
                    if (r.startSec <= last.endSec + EPS) {
                        last.endSec = Math.max(last.endSec, r.endSec);
                    } else {
                        merged.push({ ...r });
                    }
                }
                bySpeaker.set(speakerId, merged);
            }

            return bySpeaker;
        },
        [projectId, projectTracksSignature, speakerOverridesVersion],
    );

    const runExports = useCallback(
        async (speakersSnapshot: DecomposeSpeakerRuntime[]) => {
            const compId = selectedCompositionId;
            const currentProjectId = projectId;
            const sourceTrackId = String(eligibility.sourceTrackId ?? "").trim();
            const sourceIndex = Number(eligibility.sourceIndex ?? 0);
            if (!compId || !currentProjectId || !sourceTrackId) return;

            const scriptClipLive: any = CompositionService.getScriptLayerClip?.();
            if (!scriptClipLive || String(scriptClipLive?.type ?? "") !== "script") {
                setWorkflow((prev) =>
                    prev
                        ? {
                              ...prev,
                              step: "error",
                              error: "Script clip is not available.",
                          }
                        : prev,
                );
                return;
            }

            try {
                await CompositionBridge.syncScriptLayerStore();
            } catch {}
            const detachedExportSeed = buildDetachedExportSeed();

            const applyRowPatch = (
                prev: DecomposeWorkflowState,
                rowKey: string,
                patch: Partial<DecomposeExportRuntime>,
            ): DecomposeWorkflowState => {
                const idx = prev.exports.findIndex((row) => row.key === rowKey);
                if (idx < 0) return prev;
                const current = prev.exports[idx];
                const nextRow = { ...current, ...patch };
                let changed = false;
                for (const [k, v] of Object.entries(patch)) {
                    if ((current as any)[k] !== v) {
                        changed = true;
                        break;
                    }
                }
                if (!changed) return prev;
                const nextExports = [...prev.exports];
                nextExports[idx] = nextRow;
                return {
                    ...prev,
                    exports: nextExports,
                };
            };

            const queuedProgressPatches = new Map<string, Partial<DecomposeExportRuntime>>();
            let progressFlushRafId: number | null = null;
            let progressFlushTimer: ReturnType<typeof setTimeout> | null = null;

            const flushQueuedProgress = () => {
                if (progressFlushRafId != null && typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
                    window.cancelAnimationFrame(progressFlushRafId);
                }
                if (progressFlushTimer != null) {
                    clearTimeout(progressFlushTimer);
                }
                progressFlushRafId = null;
                progressFlushTimer = null;

                if (queuedProgressPatches.size === 0) return;
                const patches = Array.from(queuedProgressPatches.entries());
                queuedProgressPatches.clear();

                setWorkflow((prev) => {
                    if (!prev) return prev;
                    let next = prev;
                    for (const [rowKey, patch] of patches) {
                        next = applyRowPatch(next, rowKey, patch);
                    }
                    return next;
                });
            };

            const scheduleProgressFlush = () => {
                if (progressFlushRafId != null || progressFlushTimer != null) return;
                if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
                    progressFlushRafId = window.requestAnimationFrame(() => {
                        progressFlushRafId = null;
                        flushQueuedProgress();
                    });
                    return;
                }
                progressFlushTimer = setTimeout(() => {
                    progressFlushTimer = null;
                    flushQueuedProgress();
                }, 16);
            };

            const updateRow = (rowKey: string, patch: Partial<DecomposeExportRuntime>, opts?: { defer?: boolean }) => {
                if (opts?.defer) {
                    const existing = queuedProgressPatches.get(rowKey) ?? {};
                    queuedProgressPatches.set(rowKey, { ...existing, ...patch });
                    scheduleProgressFlush();
                    return;
                }
                setWorkflow((prev) => {
                    if (!prev) return prev;
                    return applyRowPatch(prev, rowKey, patch);
                });
            };

            setWorkflow({
                step: "export",
                speakers: speakersSnapshot,
                currentCropSpeakerIndex: null,
                error: null,
                exports: speakersSnapshot.map((s) => ({
                    key: s.key,
                    speakerName: s.speakerName,
                    status: "pending",
                    progressPct: 0,
                })),
            });

            let speakerRangesById: Map<string, Array<{ startSec: number; endSec: number }>> = new Map();
            try {
                speakerRangesById = await collectSpeakerRanges({
                    scriptClip: scriptClipLive,
                    sourceTrackId,
                    sourceIndex,
                    speakers: speakersSnapshot,
                });
            } catch (e: any) {
                const message = String(e?.message ?? e ?? "Failed to collect speaker ranges.");
                setWorkflow((prev) =>
                    prev
                        ? {
                              ...prev,
                              step: "error",
                              error: message,
                              exports: prev.exports.map((row) => ({
                                  ...row,
                                  status: row.status === "complete" ? row.status : "error",
                                  message: row.message || message,
                              })),
                          }
                        : prev,
                );
                return;
            }

            const sourceHasVideo = Boolean(eligibility.sourceHasVideo);
            const selectedTitle = String(selectedCompositionTitle ?? "Composition").trim() || "Composition";
            const decomposeAudioSettings = resolveDecomposeAudioExportSettings({
                defaultFormat: ProjectAssetsStore.projectDefaultAudioExportFormat,
                defaultSampleRate: ProjectAssetsStore.projectDefaultAudioExportSampleRate,
            });

            const runSingleSpeakerExport = async (speaker: DecomposeSpeakerRuntime) => {
                let detachedComp: any | null = null;
                let opfsDir: FileSystemDirectoryHandle | null = null;
                let stagedName = "";
                const audioFormat = decomposeAudioSettings.format;
                const extension = sourceHasVideo ? "mp4" : audioFormat;
                try {
                    updateRow(speaker.key, { status: "exporting", progressPct: 0, message: "Preparing detached export composition..." });

                    detachedComp = await createDetachedExportComposition({
                        compositionId: compId,
                        resolveAssetUrl: resolveAssetUrlRef.current,
                        resolveEnhancedAudioUrl: async (trackId: string) => {
                            try {
                                return await getEnhancedAudioUrlRef.current({ trackId });
                            } catch {
                                return undefined;
                            }
                        },
                        captionsWordsByClipId: detachedExportSeed.captionsWordsByClipId,
                        captionsConfigByClipId: detachedExportSeed.captionsConfigByClipId,
                        textConfigByClipId: detachedExportSeed.textConfigByClipId,
                        liveSnapshot: detachedExportSeed.liveSnapshot,
                    });

                    const detachedScriptClipId = String((detachedComp as any)?.scriptLayer?.clipId ?? "");
                    const detachedScriptClip: any =
                        detachedScriptClipId && typeof (detachedComp as any)?.getClipById === "function"
                            ? (detachedComp as any).getClipById(detachedScriptClipId)
                            : null;
                    if (!detachedScriptClip) {
                        throw new Error("Could not find script clip in detached export composition.");
                    }

                    const additionalMuteRanges: any[] = [];
                    for (const other of speakersSnapshot) {
                        if (other.key === speaker.key) continue;
                        const ranges = speakerRangesById.get(other.key) ?? [];
                        for (let rangeIdx = 0; rangeIdx < ranges.length; rangeIdx++) {
                            const r = ranges[rangeIdx];
                            additionalMuteRanges.push({
                                id: `decompose_${other.key}_${rangeIdx}_${r.startSec.toFixed(4)}_${r.endSec.toFixed(4)}`,
                                startSec: Number(r.startSec),
                                endSec: Number(r.endSec),
                                sourceIndex: other.sourceIndex,
                                diarizationSpeakerId: other.diarizationSpeakerIds?.[0],
                                speakerId: other.speakerEntityId,
                                speakerName: other.speakerName,
                            });
                        }
                    }

                    const existingMuteRanges = Array.isArray((detachedScriptClip as any)?.speakerMuteRanges)
                        ? ((detachedScriptClip as any).speakerMuteRanges as any[])
                        : [];
                    detachedScriptClip.applyPersistedEdits({
                        speakerMuteRanges: [...existingMuteRanges, ...additionalMuteRanges],
                    });

                    const applyFitSceneEditorForSource = () => {
                        const compWidth = Number((detachedComp as any)?.settings?.width ?? (detachedComp as any)?.width ?? NaN);
                        const compHeight = Number((detachedComp as any)?.settings?.height ?? (detachedComp as any)?.height ?? NaN);
                        if (!(Number.isFinite(compWidth) && Number.isFinite(compHeight) && compWidth > 0 && compHeight > 0)) return;

                        const sourceVisual =
                            typeof (detachedScriptClip as any)?.getVideoSourceVisual === "function"
                                ? (detachedScriptClip as any).getVideoSourceVisual(sourceIndex)
                                : undefined;
                        const sourceWidth = Number((sourceVisual as any)?.width ?? NaN);
                        const sourceHeight = Number((sourceVisual as any)?.height ?? NaN);
                        if (!(Number.isFinite(sourceWidth) && Number.isFinite(sourceHeight) && sourceWidth > 0 && sourceHeight > 0)) return;

                        const scaler = Math.min(compWidth / sourceWidth, compHeight / sourceHeight);
                        if (!Number.isFinite(scaler) || scaler <= 0) return;

                        try {
                            if (typeof (detachedScriptClip as any)?.clearSceneVisualOverrideKeysForSource === "function") {
                                (detachedScriptClip as any).clearSceneVisualOverrideKeysForSource(sourceIndex, ["x", "y", "width", "height"]);
                            }
                        } catch {}
                        try {
                            if (typeof (detachedScriptClip as any)?.updateVideoSourceTransform === "function") {
                                (detachedScriptClip as any).updateVideoSourceTransform(sourceIndex, {
                                    x: compWidth / 2,
                                    y: compHeight / 2,
                                    width: sourceWidth * scaler,
                                    height: sourceHeight * scaler,
                                });
                            }
                        } catch {}
                    };

                    if (sourceHasVideo) {
                        const crop = normalizeDecomposeCrop(speaker.crop);
                        try { detachedScriptClip.clearSceneVisualOverrideKeysForSource(sourceIndex, ["crop"]); } catch {}
                        detachedScriptClip.setVideoSourceCrop(sourceIndex, crop);
                        applyFitSceneEditorForSource();
                    }

                    if (typeof navigator === "undefined" || typeof (navigator as any)?.storage?.getDirectory !== "function") {
                        throw new Error("Browser storage API is unavailable for export.");
                    }
                    const root: FileSystemDirectoryHandle = await (navigator as any).storage.getDirectory();
                    opfsDir = await root.getDirectoryHandle("separate-speakers-exports", { create: true });
                    const uniqueId =
                        (globalThis as any)?.crypto?.randomUUID?.() ??
                        `${Date.now()}_${Math.random().toString(16).slice(2)}`;
                    stagedName = `${uniqueId}_${sanitizeFilenamePart(speaker.speakerName)}.${extension}`;
                    const fileHandle = await opfsDir.getFileHandle(stagedName, { create: true });

                    if (sourceHasVideo) {
                        const qualityPreset = VIDEO_QUALITY_PRESETS[ExportStore.selectedQuality];
                        const encoder = new MbEncoder(detachedComp, {
                            fps: detachedComp.fps,
                            resolution: 1,
                            videoBitrate: qualityPreset.videoBitrate,
                            audioBitrate: qualityPreset.audioBitrate,
                            debug: false,
                        });
                        updateRow(speaker.key, {
                            status: "exporting",
                            message: "Encoding video...",
                        });
                        await encoder.render(fileHandle, {
                            onProgress: (progress, total) => {
                                const pct = total > 0 ? (Number(progress) / Number(total)) * 100 : 0;
                                updateRow(speaker.key, {
                                    progressPct: Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0,
                                }, { defer: true });
                            },
                        });
                    } else {
                        const encoder = new MbAudioEncoder(detachedComp, {
                            format: decomposeAudioSettings.format,
                            bitrate: decomposeAudioSettings.bitrate,
                            channels: decomposeAudioSettings.channels,
                            sampleRate: decomposeAudioSettings.sampleRate,
                        });
                        updateRow(speaker.key, {
                            status: "exporting",
                            message: "Encoding audio...",
                        });
                        await encoder.render(fileHandle, {
                            onProgress: (progress, total) => {
                                const pct = total > 0 ? (Number(progress) / Number(total)) * 100 : 0;
                                updateRow(speaker.key, {
                                    progressPct: Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0,
                                }, { defer: true });
                            },
                        });
                    }

                    flushQueuedProgress();
                    updateRow(speaker.key, { status: "uploading", message: "Uploading as a new project track..." });

                    const stagedFile = await fileHandle.getFile();
                    const uploadName = `${sanitizeFilenamePart(selectedTitle)} - ${sanitizeFilenamePart(speaker.speakerName)}.${extension}`;
                    const uploadFile = new File(
                        [stagedFile],
                        uploadName,
                        {
                            type:
                                stagedFile.type ||
                                (sourceHasVideo
                                    ? "video/mp4"
                                    : audioFormat === "wav"
                                        ? "audio/wav"
                                        : "audio/mp4"),
                        },
                    );

                    const assetId = await uploadAssetRef.current({
                        projectId: currentProjectId,
                        projectType: LibrettoProjectTypes.ProjectTypeEditor,
                        file: uploadFile,
                        filetype: sourceHasVideo ? "video" : "audio",
                        languageCode: "auto",
                        assetTitle: `${selectedTitle} - ${speaker.speakerName}`,
                    });

                    updateRow(speaker.key, {
                        status: "complete",
                        progressPct: 100,
                        message: "Uploaded",
                        assetId: String(assetId ?? ""),
                    });
                } catch (e: any) {
                    flushQueuedProgress();
                    const message = String(e?.message ?? e ?? "Export failed.");
                    updateRow(speaker.key, { status: "error", message });
                } finally {
                    try {
                        await detachedComp?.dispose?.();
                    } catch {}
                    try {
                        if (opfsDir && stagedName) {
                            await opfsDir.removeEntry(stagedName);
                        }
                    } catch {}
                }
            };

            await Promise.all(speakersSnapshot.map((speaker) => runSingleSpeakerExport(speaker)));
            flushQueuedProgress();

            setWorkflow((prev) => {
                if (!prev) return prev;
                const hasErrors = prev.exports.some((row) => row.status === "error");
                return {
                    ...prev,
                    step: hasErrors ? "error" : "complete",
                    error: hasErrors ? "One or more speaker exports failed." : null,
                };
            });
        },
        [
            collectSpeakerRanges,
            eligibility.sourceHasVideo,
            eligibility.sourceIndex,
            eligibility.sourceTrackId,
            projectId,
            selectedCompositionId,
            selectedCompositionTitle,
        ],
    );

    const beginCropForIndex = useCallback(
        (speakerIndex: number) => {
            const scriptClip: any = CompositionService.getScriptLayerClip?.();
            if (!scriptClip || String(scriptClip?.type ?? "") !== "script") return;
            const clipId = String(scriptClip.id ?? "");
            if (!clipId) return;
            const speakers = workflow?.speakers ?? [];
            const speaker = speakers[speakerIndex];
            if (!speaker) return;
            ApplicationStore.setSelectedVisualElement({ clipId, sourceIndex: speaker.sourceIndex });
            ApplicationStore.setCropScope("all");
            const initialCrop = speaker.crop ?? { x: 0, y: 0, width: 1, height: 1 };
            ApplicationStore.enterCropMode(initialCrop);
        },
        [workflow],
    );

    const startWorkflow = useCallback(async (): Promise<boolean> => {
        const current = await evaluateEligibility({ force: true });
        if (!current.enabled) return false;
        const speakers = current.speakerGroups;
        setWorkflow({
            step: "assign",
            speakers,
            currentCropSpeakerIndex: null,
            exports: [],
            error: null,
        });
        return true;
    }, [evaluateEligibility]);

    const validateAssignments = useCallback(() => {
        const resolvedSpeakers = resolveSpeakers(
            eligibility.speakerDiarizationIds,
            eligibility.sourceIndex,
        );
        const groupedSpeakers = resolvedSpeakers.groupedSpeakers;

        setWorkflow((prev) => {
            const cropByKey = new Map<string, { x: number; y: number; width: number; height: number } | undefined>();
            for (const s of prev?.speakers ?? []) cropByKey.set(s.key, s.crop);
            const merged = groupedSpeakers.map((s) => ({ ...s, crop: cropByKey.get(s.key) }));

            if (resolvedSpeakers.unassignedDiarizationIds.length > 0) {
                return {
                    step: "assign",
                    speakers: merged,
                    currentCropSpeakerIndex: null,
                    exports: [],
                    error: "Assign all speaker tags in the transcript before continuing.",
                };
            }

            if (merged.length < MIN_DECOMPOSE_SPEAKERS || merged.length > MAX_DECOMPOSE_SPEAKERS) {
                const countLabel = merged.length === 1 ? "1 assigned speaker" : `${merged.length} assigned speakers`;
                return {
                    step: "assign",
                    speakers: merged,
                    currentCropSpeakerIndex: null,
                    exports: [],
                    error: `Requires ${MIN_DECOMPOSE_SPEAKERS}-${MAX_DECOMPOSE_SPEAKERS} assigned speakers (${countLabel}).`,
                };
            }

            if (!eligibility.sourceHasVideo) {
                void runExports(merged);
                return {
                    step: "export",
                    speakers: merged,
                    currentCropSpeakerIndex: null,
                    exports: merged.map((s) => ({
                        key: s.key,
                        speakerName: s.speakerName,
                        status: "pending",
                        progressPct: 0,
                    })),
                    error: null,
                };
            }

            const firstCropIndex = 0;
            window.setTimeout(() => beginCropForIndex(firstCropIndex), 0);
            return {
                step: "crop",
                speakers: merged,
                currentCropSpeakerIndex: firstCropIndex,
                exports: [],
                error: null,
            };
        });
    }, [beginCropForIndex, eligibility, resolveSpeakers, runExports]);

    const startCurrentCrop = useCallback(() => {
        const idx = workflow?.currentCropSpeakerIndex;
        if (workflow?.step !== "crop" || idx == null) return;
        beginCropForIndex(idx);
    }, [beginCropForIndex, workflow]);

    const onCropDoneCapture = useCallback(
        (crop: { x: number; y: number; width: number; height: number }, _scope: "current" | "all") => {
            if (!workflow || workflow.step !== "crop") return false;
            const idx = workflow.currentCropSpeakerIndex;
            if (idx == null || idx < 0 || idx >= workflow.speakers.length) return false;

            const normalizedCrop = normalizeDecomposeCrop(crop);
            const speakersAfterCrop = workflow.speakers.map((s, sIdx) =>
                sIdx === idx ? { ...s, crop: normalizedCrop } : s,
            );
            const nextIdx = speakersAfterCrop.findIndex((s, sIdx) => sIdx > idx && !s.crop);

            if (nextIdx >= 0) {
                setWorkflow((prev) =>
                    prev
                        ? {
                              ...prev,
                              speakers: speakersAfterCrop,
                              step: "crop",
                              currentCropSpeakerIndex: nextIdx,
                              error: null,
                          }
                        : prev,
                );
                window.setTimeout(() => beginCropForIndex(nextIdx), 0);
                return true;
            }

            void runExports(speakersAfterCrop);
            setWorkflow((prev) =>
                prev
                    ? {
                          ...prev,
                          speakers: speakersAfterCrop,
                          step: "export",
                          currentCropSpeakerIndex: null,
                          exports: speakersAfterCrop.map((s) => ({
                              key: s.key,
                              speakerName: s.speakerName,
                              status: "pending",
                              progressPct: 0,
                          })),
                          error: null,
                      }
                    : prev,
            );
            return true;
        },
        [beginCropForIndex, runExports, workflow],
    );

    const speakersSource: DecomposeSpeakerRuntime[] = useMemo(() => {
        if (!workflow) {
            return eligibility.speakerGroups;
        }
        if (workflow.step !== "assign") return workflow.speakers;

        const latest = resolveSpeakers(
            eligibility.speakerDiarizationIds,
            eligibility.sourceIndex,
        ).groupedSpeakers;
        const cropByKey = new Map<string, { x: number; y: number; width: number; height: number } | undefined>();
        for (const speaker of workflow.speakers) {
            cropByKey.set(speaker.key, speaker.crop);
        }
        return latest.map((speaker) => ({
            ...speaker,
            crop: cropByKey.get(speaker.key),
        }));
    }, [eligibility.sourceIndex, eligibility.speakerDiarizationIds, eligibility.speakerGroups, resolveSpeakers, workflow]);

    const speakers: DecomposeSpeakerView[] = useMemo(
        () =>
            speakersSource.map((speaker) => ({
                key: speaker.key,
                speakerName: String(speaker.speakerName || "").trim() || "New Speaker",
                assigned: String(speaker.speakerName || "").trim() !== "" && String(speaker.speakerName || "").trim() !== "New Speaker",
                color: speaker.color,
                cropCaptured: Boolean(speaker.crop),
            })),
        [speakersSource],
    );

    const exports: DecomposeExportRow[] = useMemo(
        () =>
            (workflow?.exports ?? []).map((row) => ({
                key: row.key,
                speakerName: row.speakerName,
                status: row.status,
                progressPct: row.progressPct,
                message: row.message,
            })),
        [workflow?.exports],
    );

    const disabledReason = eligibility.enabled
        ? null
        : eligibility.reason || "Checking clip eligibility...";
    const hasWorkflow = Boolean(workflow);
    const isWorkflowActive = Boolean(workflow && workflow.step !== "complete" && workflow.step !== "error");
    const isProcessing = Boolean(
        workflow?.exports?.some((row) => row.status === "exporting" || row.status === "uploading"),
    );

    return {
        disabledReason,
        hasVideo: Boolean(eligibility.sourceHasVideo),
        step: (workflow?.step ?? "assign") as DecomposeWorkflowStep,
        hasWorkflow,
        isWorkflowActive,
        isProcessing,
        speakers,
        currentCropSpeakerIndex: workflow?.currentCropSpeakerIndex ?? undefined,
        exports,
        error: workflow?.error ?? null,
        isCropModeActive: Boolean(ApplicationStore.isCropMode),
        startWorkflow,
        resetWorkflow,
        validateAssignments,
        startCurrentCrop,
        onCropDoneCapture,
    };
};

export default useDecomposeClipWorkflow;
